import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAccount, useDisconnect, useWalletClient } from 'wagmi';
import { BrowserProvider, JsonRpcSigner } from 'ethers';
import { useWeb3Modal } from '@web3modal/wagmi/react';

interface WalletContextType {
  isConnected: boolean;
  address: string | undefined;
  signer: JsonRpcSigner | null;
  chainId: number | undefined;
  connect: () => Promise<void>;
  disconnect: () => void;
  isLoading: boolean;
}

const WalletContext = createContext<WalletContextType | undefined>(undefined);

export function WalletProvider({ children }: { children: React.ReactNode }) {
  const { open } = useWeb3Modal();
  const { address, isConnected, chainId } = useAccount();
  const { disconnect } = useDisconnect();
  const { data: walletClient } = useWalletClient();
  const [signer, setSigner] = useState<JsonRpcSigner | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Convert Viem wallet client to ethers signer
  useEffect(() => {
    const getSigner = async () => {
      if (walletClient && isConnected) {
        try {
          // Create ethers provider from Viem client
          const provider = new BrowserProvider(walletClient);
          const ethersSigner = await provider.getSigner();
          setSigner(ethersSigner);
        } catch (error) {
          console.error('Error getting signer:', error);
          setSigner(null);
        }
      } else {
        setSigner(null);
      }
    };

    getSigner();
  }, [walletClient, isConnected]);

  const connect = async () => {
    setIsLoading(true);
    try {
      await open();
    } catch (error) {
      console.error('Connection error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDisconnect = () => {
    disconnect();
    setSigner(null);
  };

  return (
    <WalletContext.Provider
      value={{
        isConnected,
        address,
        signer,
        chainId,
        connect,
        disconnect: handleDisconnect,
        isLoading,
      }}
    >
      {children}
    </WalletContext.Provider>
  );
}

/**
 * Inert state for runtimes where the Web3 extension is not loaded at all.
 * Tenants with crypto disabled never mount WalletProvider (see
 * providers/Web3Providers.tsx), so a wallet-aware component rendering there
 * should report "no wallet" rather than crash the page. connect() rejects
 * instead of silently no-op'ing — a caller awaiting a connection deserves
 * to know it cannot happen here.
 */
const WALLET_UNAVAILABLE: WalletContextType = {
  isConnected: false,
  address: undefined,
  signer: null,
  chainId: undefined,
  connect: async () => {
    throw new Error('Wallet features are not enabled for this tenant');
  },
  disconnect: () => {},
  isLoading: false,
};

export function useWallet() {
  const context = useContext(WalletContext);
  return context === undefined ? WALLET_UNAVAILABLE : context;
}
